import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PRODUCT_URLS } from '@podus/utils';

import podusAiLogo from '../../landingPagesNavbar/assets/podus-ai.svg';
import podusAidLogo from '../../landingPagesNavbar/assets/podus-aids.svg';
import podusAppointmentLogo from '../../landingPagesNavbar/assets/podus-appointment.svg';
import podusContractLogo from '../../landingPagesNavbar/assets/podus-contract.svg';
import podusCheckerLogo from '../../landingPagesNavbar/assets/podus-cheaker.svg';
import podusMultidoorLogo from '../../landingPagesNavbar/assets/podus-multidoor.svg';

export type AppId =
    | 'podus-ai'
    | 'podus-aid'
    | 'podus-appointment'
    | 'podus-contract'
    | 'podus-checker'
    | 'podus-multidoor'
    | 'iris';

export interface Product {
    id: AppId;
    logo: string;
    title: string;
    description: string;
    href: string;
}

const ALL_PRODUCTS: Product[] = [
    {
        id: 'podus-ai',
        logo: podusAiLogo,
        title: 'Podus AI',
        description: 'Ask legal questions, get instant AI-powered answers now.',
        href: PRODUCT_URLS.chatpodusai,
    },
    {
        id: 'podus-aid',
        logo: podusAidLogo,
        title: 'Podus Aid',
        description: 'Legal aid access made simple, fast, and fair.',
        href: PRODUCT_URLS.podusaid,
    },
    {
        id: 'podus-appointment',
        logo: podusAppointmentLogo,
        title: 'Podus Appointment',
        description: 'Book appointments with verified legal professionals.',
        href: PRODUCT_URLS.podusappointment,
    },
    {
        id: 'podus-contract',
        logo: podusContractLogo,
        title: 'Podus Contract',
        description: 'Create, review, and manage legal contracts with ease.',
        href: PRODUCT_URLS.poduscontract,
    },
    {
        id: 'podus-checker',
        logo: podusCheckerLogo,
        title: 'Podus Checker',
        description: 'Background investigation and compliance checking platform.',
        href: PRODUCT_URLS.poduschecker,
    },
    {
        id: 'podus-multidoor',
        logo: podusMultidoorLogo,
        title: 'Podus Multidoor',
        description: 'File, track, and resolve disputes through digital court-connected resolution.',
        href: PRODUCT_URLS.podusmultidoor,
    },
];

export interface SidebarExplorerProps {
    currentApp: AppId;
    products?: Product[];
    featuredCount?: number;
    onNavigate?: (product: Product) => void;
    className?: string;
}

export default function SidebarExplorer({
    currentApp,
    products = ALL_PRODUCTS,
    featuredCount = 2,
    onNavigate,
    className = '',
}: SidebarExplorerProps) {
    const [showProducts, setShowProducts] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 1023px)');
        setIsMobile(mq.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const others = products.filter((p) => p.id !== currentApp);
    const featured = others.slice(0, featuredCount);

    const togglePanel = useCallback(() => setShowProducts((v) => !v), []);

    useEffect(() => {
        if (!showProducts) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowProducts(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [showProducts]);

    useEffect(() => {
        if (!showProducts) return;
        const onClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (
                triggerRef.current &&
                !triggerRef.current.contains(t) &&
                popupRef.current &&
                !popupRef.current.contains(t)
            )
                setShowProducts(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [showProducts]);

    const getPosition = (): React.CSSProperties => {
        if (!triggerRef.current) return { display: 'none' };
        const r = triggerRef.current.getBoundingClientRect();
        return { position: 'fixed', left: r.right + 12, bottom: 16, maxHeight: 'calc(100vh - 32px)', zIndex: 9999 };
    };

    const renderLink = (product: Product, children: React.ReactNode, cls: string) =>
        onNavigate ? (
            <button key={product.id} type="button" onClick={() => onNavigate(product)} className={cls}>
                {children}
            </button>
        ) : (
            <a key={product.id} href={product.href} target="_blank" rel="noopener noreferrer" className={cls}>
                {children}
            </a>
        );

    return (
        <div className={className}>
            <p className="mb-3 text-base font-bold text-typo">Explore</p>

            {/* ── Featured items ── */}
            <div className="space-y-1">
                {featured.map((item) =>
                    renderLink(
                        item,
                        <>
                            <img src={item.logo} alt={item.title} className="h-6 w-6 shrink-0 object-contain" />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-typo">{item.title}</p>
                                <p className="text-xs leading-snug text-[#888]">{item.description}</p>
                            </div>
                            <svg
                                className="shrink-0 text-[#888]"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </>,
                        'flex w-full cursor-pointer items-center gap-3 rounded-xl px-2 py-3 text-left no-underline transition-colors hover:bg-[#f5f5f5]',
                    ),
                )}

                {/* ── All Podus Tools trigger ── */}
                <div
                    ref={triggerRef}
                    role="button"
                    tabIndex={0}
                    onClick={togglePanel}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            togglePanel();
                        }
                    }}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-2 py-3 transition-colors hover:bg-[#f5f5f5]"
                >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <circle cx="5" cy="5" r="2.5" fill="#ff6600" />
                            <circle cx="12" cy="5" r="2.5" fill="#ff6600" />
                            <circle cx="19" cy="5" r="2.5" fill="#ff6600" />
                            <circle cx="5" cy="12" r="2.5" fill="#ff6600" />
                            <circle cx="12" cy="12" r="2.5" fill="#ff6600" />
                            <circle cx="19" cy="12" r="2.5" fill="#ff6600" />
                            <circle cx="5" cy="19" r="2.5" fill="#ff6600" />
                            <circle cx="12" cy="19" r="2.5" fill="#ff6600" />
                            <circle cx="19" cy="19" r="2.5" fill="#ff6600" />
                        </svg>
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                        <p className="text-sm font-semibold text-typo">All Podus Tools</p>
                    </div>
                    <svg
                        className="shrink-0 text-[#888]"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </div>
            </div>

            {/* ── Products panel (portal) ── */}
            {showProducts &&
                createPortal(
                    <>
                        {/* Backdrop — mobile only */}
                        {isMobile && (
                            <div className="fixed inset-0 z-9998 bg-black/30" onClick={() => setShowProducts(false)} />
                        )}

                        <div
                            ref={popupRef}
                            className={
                                isMobile
                                    ? 'sidebar-panel-up fixed inset-x-0 bottom-0 z-9999 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white shadow-[0_-4px_24px_rgba(0,0,0,0.10)]'
                                    : 'sidebar-panel-in fixed z-9999 w-[800px] max-w-[90vw] overflow-y-auto rounded-2xl bg-white shadow-[0_8px_40px_rgba(0,0,0,0.10),0_2px_8px_rgba(0,0,0,0.04)]'
                            }
                            style={isMobile ? undefined : getPosition()}
                        >
                            {/* Drag handle — mobile only */}
                            {isMobile && (
                                <div className="flex justify-center pb-1 pt-3">
                                    <div className="h-1 w-10 rounded-full bg-[#e0e0e0]" />
                                </div>
                            )}

                            {/* Header */}
                            <div className="flex items-center justify-between px-6 pb-2 pt-5 lg:px-8 lg:pt-7">
                                <h3 className="text-xl font-bold text-typo">Products</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowProducts(false)}
                                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background text-[#555] transition-colors hover:bg-[#e5e5e5]"
                                    aria-label="Close"
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>

                            {/* Grid — 2 cols mobile, 3 cols desktop */}
                            <div className="grid grid-cols-2 gap-0 px-3 pb-6 pt-3 lg:grid-cols-3 lg:px-4">
                                {others.map((product) =>
                                    renderLink(
                                        product,
                                        <>
                                            <div className="mb-3 flex items-center gap-3">
                                                <img
                                                    src={product.logo}
                                                    alt={product.title}
                                                    className="mt-0.5 h-7 w-7 shrink-0 object-contain"
                                                />
                                                <p className="flex-1 text-[15px] font-bold leading-snug text-[#171717]">
                                                    {product.title}
                                                </p>
                                                <svg
                                                    className="mt-1 shrink-0 text-[#bbb] transition-colors group-hover:text-[#888]"
                                                    width="15"
                                                    height="15"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M9 18l6-6-6-6" />
                                                </svg>
                                            </div>
                                            <p className="text-[13px] leading-relaxed text-[#918EA4]">
                                                {product.description}
                                            </p>
                                        </>,
                                        'group block cursor-pointer rounded-2xl p-5 text-left no-underline transition-colors hover:bg-[#f5f5f5]',
                                    ),
                                )}
                            </div>
                        </div>
                    </>,
                    document.body,
                )}
        </div>
    );
}
